import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { SITE_NAME, SITE_LOGO_URL } from "@/mocks/seo";
import { useRouteLanguage, useLocalizedLinks } from "@/hooks/useLocale";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";
import { guideHeroImage } from "@/mocks/images";

const TOC_ITEMS = [
  { id: "what-is-a-usfans-spreadsheet", key: "article.sections.intro.title" },
  { id: "how-it-works", key: "article.sections.how.title" },
  { id: "how-to-search", key: "article.sections.search.title" },
  { id: "product-categories", key: "article.sections.categories.title" },
  { id: "spreadsheet-vs-search", key: "article.sections.compare.title" },
  { id: "safety", key: "article.sections.legit.title" },
  { id: "verify-information", key: "article.sections.verify.title" },
  { id: "faq", key: "article.sections.faq.title" },
];

export default function Article() {
  const { t } = useTranslation();
  useRouteLanguage();
  const { to: linkTo, lang } = useLocalizedLinks();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfanssheet.live";

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: t("article.title"),
    description: t("article.subtitle"),
    image: guideHeroImage,
    datePublished: "2026-08-20",
    dateModified: "2026-08-20",
    author: { "@type": "Organization", name: "USFansSheet Editorial Team" },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: SITE_LOGO_URL },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/${lang}/usfans-spreadsheet-guide`,
    },
    inLanguage: lang,
  };

  const howSteps = t("article.sections.how.steps", { returnObjects: true }) as string[];
  const searchSteps = t("article.sections.search.steps", { returnObjects: true }) as string[];
  const verifySteps = t("article.sections.verify.steps", { returnObjects: true }) as string[];
  const compareHeaders = t("article.sections.compare.headers", { returnObjects: true }) as string[];
  const compareRows = t("article.sections.compare.rows", { returnObjects: true }) as string[][];
  const faqItems = t("article.sections.faq.items", { returnObjects: true }) as { q: string; a: string }[];

  return (
    <>
      <JsonLd data={articleJsonLd} />
      <Header />
      <main className="bg-background-50">
        {/* Breadcrumb */}
        <div className="mx-auto w-full max-w-4xl px-4 pt-6 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-600">
            <Link to={linkTo("/")} className="transition-colors hover:text-primary-700">
              {t("article.breadcrumbHome")}
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
            <span className="text-foreground-900">{t("article.breadcrumbCurrent")}</span>
          </nav>
        </div>

        {/* Article header */}
        <article className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6">
          <div className="overflow-hidden rounded-lg">
            <img
              src={guideHeroImage}
              alt={t("article.title")}
              title={t("article.title")}
              className="h-56 w-full object-cover object-top md:h-80"
            />
          </div>

          <h1 className="mt-8 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {t("article.title")}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-lg">
            {t("article.subtitle")}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-user-line text-sm"></i>
              </span>
              {t("article.meta.author")}
            </span>
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-time-line text-sm"></i>
              </span>
              {t("article.meta.readTime")}
            </span>
            <span>{t("article.meta.published")}</span>
            <span>{t("article.meta.updated")}</span>
          </div>

          {/* Quick answer */}
          <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
            <span className="flex items-center gap-2 font-heading text-sm font-semibold text-primary-800">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-flashlight-line text-sm"></i>
              </span>
              {t("article.quickAnswerLabel")}
            </span>
            <p className="mt-2 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.quickAnswer")}
            </p>
          </div>

          {/* Table of contents */}
          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("article.tocLabel")}</h2>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {TOC_ITEMS.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </span>
                    {t(item.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Section: Intro */}
          <section id="what-is-a-usfans-spreadsheet" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.intro.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.intro.p1")}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.intro.p2")}
            </p>
          </section>

          {/* Section: How it works */}
          <section id="how-it-works" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.how.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.how.p1")}
            </p>
            <ol className="mt-5 space-y-3">
              {howSteps.map((step, i) => (
                <li key={step} className="flex items-start gap-3 text-sm text-foreground-800 md:text-base">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Section: How to search */}
          <section id="how-to-search" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.search.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.search.p1")}
            </p>
            <ul className="mt-5 space-y-3">
              {searchSteps.map((step) => (
                <li key={step} className="flex items-start gap-3 text-sm text-foreground-800 md:text-base">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                    <i className="ri-checkbox-circle-line text-base"></i>
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section: Product categories */}
          <section id="product-categories" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.categories.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.categories.p1")}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={linkTo("/#categories")}
                  className="whitespace-nowrap rounded-full border border-secondary-200 bg-secondary-50 px-4 py-1.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-100"
                >
                  {t(`categories.items.${cat.id}`)}
                </Link>
              ))}
            </div>
            <p className="mt-4 text-xs text-foreground-600">{t("article.sections.categories.note")}</p>
          </section>

          {/* Section: Comparison table */}
          <section id="spreadsheet-vs-search" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.compare.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.compare.p1")}
            </p>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {compareHeaders.map((h) => (
                      <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-900">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((row, i) => (
                    <tr key={row[0]} className={i % 2 === 0 ? "bg-background-50" : "bg-background-100/60"}>
                      {row.map((cell, j) => (
                        <td
                          key={`${row[0]}-${j}`}
                          className={`px-4 py-3 ${
                            j === 0 ? "font-medium text-foreground-950" : "text-foreground-700"
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section: Legit / Safety */}
          <section id="safety" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.legit.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.legit.p1")}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.legit.p2")}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.legit.p3")}
            </p>
            <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-4">
              <p className="flex items-start gap-2 text-sm text-accent-900">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                  <i className="ri-alert-line text-base"></i>
                </span>
                {t("article.sections.legit.safetyNote")}
              </p>
            </div>
          </section>

          {/* Section: Verify */}
          <section id="verify-information" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.verify.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {t("article.sections.verify.p1")}
            </p>
            <ul className="mt-5 space-y-3">
              {verifySteps.map((step) => (
                <li key={step} className="flex items-start gap-3 text-sm text-foreground-800 md:text-base">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                    <i className="ri-check-double-line text-base"></i>
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section: FAQ */}
          <section id="faq" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.sections.faq.title")}
            </h2>
            <div className="mt-5 space-y-3">
              {faqItems.map((item) => (
                <div key={item.q} className="rounded-lg border border-background-200 bg-background-100 p-4">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                    {item.q}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related + CTA */}
          <section className="mt-10">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                {t("article.sections.related.title")}
              </h2>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-6">
                <Link
                  to={linkTo("/")}
                  className="flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </span>
                  {t("article.sections.related.homeText")}
                </Link>
                <Link
                  to={linkTo("/#safety")}
                  className="flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-shield-check-line text-sm"></i>
                  </span>
                  {t("article.sections.related.safetyText")}
                </Link>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-primary-950 p-6 text-center md:p-8">
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                {t("article.sections.cta.title")}
              </h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-background-100/80">
                {t("article.sections.cta.text")}
              </p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
              >
                {t("article.sections.cta.button")}
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-arrow-right-up-line text-base"></i>
                </span>
              </a>
            </div>
          </section>

          <p className="mt-8 border-t border-background-200 pt-6 text-xs leading-relaxed text-foreground-600">
            {t("article.disclaimer")}
          </p>
        </article>
      </main>
      <Footer />
    </>
  );
}